import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useSearchParams } from "react-router";
import { getErrorDetail, getErrorMessage } from "#/api/errors";
import { chatsByWorkspace } from "#/api/queries/chats";
import { workspacePermissionsByOrganization } from "#/api/queries/organizations";
import { templates, templateVersionRoot } from "#/api/queries/templates";
import { workspaces } from "#/api/queries/workspaces";
import { useFilter } from "#/components/Filter/Filter";
import { toast } from "#/components/Toaster/toast";
import { useAuthenticated } from "#/hooks/useAuthenticated";
import { usePagination } from "#/hooks/usePagination";
import { ACTIVE_BUILD_STATUSES } from "#/modules/workspaces/status";
import { pageTitle } from "#/utils/page";
import { BatchDeleteConfirmation } from "./BatchDeleteConfirmation";
import { BatchStopConfirmation } from "./BatchStopConfirmation";
import { BatchUpdateModalForm } from "./BatchUpdateModalForm";
import { useBatchActions } from "./batchActions";
import { WorkspacesPageView } from "./WorkspacesPageView";

// To reduce the number of fetches, we reduce the fetch interval if there are no
// active workspace builds.
const ACTIVE_BUILDS_REFRESH_INTERVAL = 5_000;
const NO_ACTIVE_BUILDS_REFRESH_INTERVAL = 30_000;

function useSafeSearchParams() {
	// Have to wrap setSearchParams because React Router doesn't guarantee
	// a stable reference for setSearchParams across renders.
	const [searchParams, setSearchParams] = useSearchParams();
	const setterRef = useRef(setSearchParams);
	useLayoutEffect(() => {
		setterRef.current = setSearchParams;
	}, [setSearchParams]);
	const stableSetSearchParams = useCallback(
		(...args: Parameters<typeof setSearchParams>) => setterRef.current(...args),
		[],
	);
	// Need this to return a tuple, not a plain array. Using "as const"
	// would make it readonly and cause type mismatches at call sites.
	return [searchParams, stableSetSearchParams] as ReturnType<
		typeof useSearchParams
	>;
}

type BatchAction = "delete" | "stop" | "update";

const WorkspacesPage: React.FC = () => {
	const queryClient = useQueryClient();
	// We have to be careful with how we use useSearchParams or any other
	// derived hooks. The URL is global state, but each call to useSearchParams
	// creates a different, contradictory source of truth for what the URL
	// should look like. We need to make sure that we only mount the hook once
	// per page
	const [searchParams, setSearchParams] = useSafeSearchParams();
	// Always need to make sure that we reset the checked workspaces each time
	// the filtering or pagination changes, as that will almost always change
	// which workspaces are shown on screen and which can be interacted with
	const [checkedWorkspaceIds, setCheckedWorkspaceIds] = useState(
		new Set<string>(),
	);
	const resetChecked = () => {
		if (checkedWorkspaceIds.size !== 0) {
			setCheckedWorkspaceIds(new Set());
		}
	};

	const pagination = usePagination({
		searchParams,
		onSearchParamsChange: (newParams) => {
			setSearchParams(newParams);
			resetChecked();
		},
	});
	const { permissions, user: me } = useAuthenticated();
	const templatesQuery = useQuery(templates());
	const workspacePermissionsQuery = useQuery(
		workspacePermissionsByOrganization(
			templatesQuery.data?.map((template) => template.organization_id),
			me.id,
		),
	);

	// Filter templates based on workspace creation permission
	const filteredTemplates = useMemo(() => {
		if (!templatesQuery.data || !workspacePermissionsQuery.data) {
			return templatesQuery.data;
		}

		return templatesQuery.data.filter((template) => {
			const workspacePermission =
				workspacePermissionsQuery.data[template.organization_id];
			return workspacePermission?.createWorkspaceForUserID;
		});
	}, [templatesQuery.data, workspacePermissionsQuery.data]);

	const filter = useFilter({
		fallbackFilter: "user:me",
		searchParams,
		onSearchParamsChange: setSearchParams,
		onUpdate: () => {
			pagination.goToPage(1);
			resetChecked();
		},
	});

	const workspacesQueryOptions = workspaces({
		limit: pagination.limit,
		offset: pagination.offset,
		q: filter.query,
	});
	const { data, error, refetch } = useQuery({
		...workspacesQueryOptions,
		refetchInterval: ({ state }) => {
			if (state.error) return false;

			// Default to 5s interval until first fetch completes
			if (!state.data) return ACTIVE_BUILDS_REFRESH_INTERVAL;

			// Check if any workspace has an active build
			const hasActiveBuilds = state.data.workspaces?.some((workspace) => {
				const status = workspace.latest_build.status;
				return ACTIVE_BUILD_STATUSES.includes(status);
			});

			// Poll every 5s if there are active builds, otherwise every 30s
			return hasActiveBuilds
				? ACTIVE_BUILDS_REFRESH_INTERVAL
				: NO_ACTIVE_BUILDS_REFRESH_INTERVAL;
		},
		refetchOnWindowFocus: "always",
	});

	const workspaceIds = useMemo(
		() => data?.workspaces?.map((w) => w.id) ?? [],
		[data?.workspaces],
	);
	const chatsByWorkspaceQuery = useQuery({
		...chatsByWorkspace(workspaceIds),
		// Only fetch chat lookups for users who can actually create chats;
		// the endpoint still runs a DB query + RBAC post-filter and the
		// AgentsNavItem / chat link UI is already hidden for users without
		// this permission, so the query would return nothing useful for them.
		enabled: permissions.createChat && workspaceIds.length > 0,
	});

	const [activeBatchAction, setActiveBatchAction] = useState<BatchAction>();
	const batchActions = useBatchActions({
		onSuccess: async () => {
			await refetch();
			resetChecked();
		},
	});

	const checkedWorkspaces =
		data?.workspaces.filter((w) => checkedWorkspaceIds.has(w.id)) ?? [];

	// Bulk stop only affects running workspaces, so the confirmation dialog and
	// the mutation should both operate on that subset to avoid over-reporting
	// how many workspaces will actually be stopped.
	const workspacesToStop = checkedWorkspaces.filter(
		(w) => w.latest_build.status === "running",
	);

	return (
		<>
			<title>{pageTitle("Workspaces")}</title>

			<WorkspacesPageView
				canCreateTemplate={permissions.createTemplates}
				canCreateWorkspace={permissions.createWorkspace}
				canChangeVersions={permissions.updateTemplates}
				checkedWorkspaces={checkedWorkspaces}
				chatsByWorkspace={chatsByWorkspaceQuery.data}
				onCheckChange={(newWorkspaces) => {
					setCheckedWorkspaceIds((current) => {
						const newIds = newWorkspaces.map((ws) => ws.id);
						const sameContent =
							current.size === newIds.length &&
							newIds.every((id) => current.has(id));
						if (sameContent) {
							return current;
						}
						return new Set(newIds);
					});
				}}
				templates={filteredTemplates}
				templatesFetchStatus={templatesQuery.status}
				workspaces={data?.workspaces}
				error={error}
				count={data?.count}
				page={pagination.page}
				limit={pagination.limit}
				onPageChange={pagination.goToPage}
				filter={filter}
				isRunningBatchAction={batchActions.isProcessing}
				onBatchDeleteTransition={() => setActiveBatchAction("delete")}
				onBatchStartTransition={() => batchActions.start(checkedWorkspaces)}
				onBatchStopTransition={() => setActiveBatchAction("stop")}
				onBatchUpdateTransition={() => {
					// Just because batch-updating can be really dangerous
					// action for running workspaces, we're going to invalidate
					// all relevant queries as a prefetch strategy before the
					// modal content is even allowed to mount.
					for (const ws of checkedWorkspaces) {
						// Our data layer is a little messy right now, so
						// there's no great way to invalidate a bunch of
						// template version queries with a single function call,
						// while also avoiding all other tangentially connected
						// resources that use the same key pattern. Have to be
						// super granular and make one call per workspace.
						queryClient.invalidateQueries({
							queryKey: [templateVersionRoot, ws.template_active_version_id],
							exact: true,
							type: "all",
						});
					}
					setActiveBatchAction("update");
				}}
				onActionSuccess={async () => {
					await queryClient.invalidateQueries({
						queryKey: workspacesQueryOptions.queryKey,
					});
				}}
				onActionError={(error) => {
					toast.error(getErrorMessage(error, "Failed to perform action."), {
						description: getErrorDetail(error),
					});
				}}
			/>

			<BatchDeleteConfirmation
				isLoading={batchActions.isProcessing}
				checkedWorkspaces={checkedWorkspaces}
				open={activeBatchAction === "delete"}
				onClose={() => setActiveBatchAction(undefined)}
				onConfirm={async () => {
					await batchActions.delete(checkedWorkspaces);
					setActiveBatchAction(undefined);
				}}
			/>

			<BatchStopConfirmation
				isLoading={batchActions.isProcessing}
				workspacesToStop={workspacesToStop}
				open={activeBatchAction === "stop"}
				onClose={() => setActiveBatchAction(undefined)}
				onConfirm={async () => {
					await batchActions.stop(workspacesToStop);
					setActiveBatchAction(undefined);
				}}
			/>

			<BatchUpdateModalForm
				open={activeBatchAction === "update"}
				workspacesToUpdate={checkedWorkspaces}
				isProcessing={batchActions.isProcessing}
				onCancel={() => setActiveBatchAction(undefined)}
				onSubmit={async () => {
					await batchActions.updateTemplateVersions({
						workspaces: checkedWorkspaces,
					});
					setActiveBatchAction(undefined);
				}}
			/>
		</>
	);
};

export default WorkspacesPage;
